import styled from '@emotion/styled';
import {EventApi} from '@fullcalendar/core';
import moment from 'moment';
import React, {useContext, useMemo, useState} from 'react';
import {mixinFlexboxColumn, mixinFlexboxRow} from 'webapp-lib/styling';

import {useEventAssignmentSummary} from '../hooks/useEventAssignmentSummary';
import {useRecordingEvent} from '../hooks/useRecordingEvent';
import {EventApiWithWorkLogProps} from '../types';

import {ActivityWarnings} from './ActivityWarnings';
import {Calendar} from './Calendar';
import {FavoritesAndRecent} from './FavoritesAndRecent';
import {Summary} from './Summary';
import {CalendarAPIContext} from './TimeTrackerShelf';
import {QuickActivity} from './quickbar/QuickActivity';

export const TimeTracker = (): React.ReactElement => {
  const [events, setEvents] = useState<EventApiWithWorkLogProps[]>([]);
  const {calendarAPI} = useContext(CalendarAPIContext);

  const {eventSummary} = useEventAssignmentSummary(
    events,
    moment(calendarAPI()?.getDate() ?? new Date())
  );
  const {recordingEvent, timer} = useRecordingEvent(calendarAPI);

  const handleEventsChanged = (updatedEvents: EventApi[]) => {
    setEvents(updatedEvents);
  };

  const quickActivityMemoized = useMemo(
    () => <QuickActivity recordingEvent={recordingEvent} timer={timer} />,
    [recordingEvent, timer]
  );

  return (
    <>
      <WithBottomDrawer>
        {quickActivityMemoized}
        <MainPanels>
          <StyledFavoriteColumn>
            <FavoritesAndRecent events={events} timer={timer} />
          </StyledFavoriteColumn>
          <StyledCalendarColumn>
            <Calendar
              handleEventsChanged={handleEventsChanged}
              initialEvents={events}
              recordingEvent={recordingEvent}
              timer={timer}
            />
            <Summary assignmentSummary={eventSummary} />
          </StyledCalendarColumn>
        </MainPanels>
        <ActivityWarnings eventsSummary={eventSummary} />
      </WithBottomDrawer>
    </>
  );
};

const MainPanels = styled.div`
  ${mixinFlexboxRow};
  flex-grow: 1;
  min-height: 0;

  padding: var(--gutter-medium);
  gap: var(--gutter);
`;

const WithBottomDrawer = styled.div`
  ${mixinFlexboxColumn};
  justify-content: space-between;
  height: 100%;
`;

const StyledFavoriteColumn = styled.div`
  ${mixinFlexboxColumn};
  max-width: 40%;
`;

const StyledCalendarColumn = styled.div`
  ${mixinFlexboxColumn};
  flex-grow: 2;
`;
